// useSchoolLockerSubmission
// -------------------------
// Custom React hook — owns the School Locker submission lifecycle for a single
// request. Two approval code paths inside RequestDetail can trigger the submit,
// so the hook also guards against re-entry (in-memory ref + DB check on mount).
//
// Extracted from RequestDetail.jsx (Phase D). Behaviour is identical.
// TypeScript source — useSchoolLockerSubmission.js is a thin re-export stub.

import React, { useState } from "react";
import { getSentSubmissionByRequestId, insertSubmission } from "../data/schoolLockerSubmissions";
import { money } from "../utils/helpers";
import { logger } from "../utils/logger";
import { getSupabaseClient } from "../utils/supabase";

interface SLLineItem {
  qty?: number | string;
  cost?: number | string;
}
interface SLUniform {
  supplier?: string;
  items?: Record<string, SLLineItem>;
  customItems?: SLLineItem[];
  mobile?: string;
  email?: string;
}
interface SLChild {
  gender?: string;
  yearLevel?: string;
  schoolName?: string;
  uniforms?: SLUniform | null;
}
interface SLReq {
  id?: number | string | null;
  refNo?: string | null;
  date?: string | null;
  shelterCode?: string | null;
  shelterName?: string | null;
  shelterRef?: string | null;
  shelterEmail?: string | null;
  phone?: string | null;
  children?: SLChild[];
}
interface SLUser {
  shelterName?: string | null;
  email?: string | null;
}
// The per-child payload pushed into the send-email request.
interface SLPushChild {
  gender: string;
  yearLevel: string;
  schoolName: string;
  items: Record<string, SLLineItem>;
  customItems: SLLineItem[];
  mobile: string;
  email: string;
}

type SLState = "idle" | "loading" | "success" | "error";

export function useSchoolLockerSubmission(req: SLReq, user: SLUser) {
  const [slSubmitState, setSlSubmitState] = useState<SLState>("idle");
  const [slSubmitMsg, setSlSubmitMsg] = useState("");
  // Synchronous lock — two approval code paths can both trigger submit, and
  // React state updates are async, so a ref blocks re-entry reliably.
  const slSubmittingRef = React.useRef(false);
  const [_slSubmission, setSlSubmission] = useState<unknown>(null);

  // Load existing School Locker submission record on mount
  React.useEffect(
    function () {
      if (!req.id) return;
      getSentSubmissionByRequestId(req.id).then(function (row) {
        if (row) {
          setSlSubmission(row);
          setSlSubmitState("success");
          setSlSubmitMsg(
            row.submitted_at
              ? new Date(row.submitted_at).toLocaleString("en-AU", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "Submitted"
          );
        }
      });
    },
    [req.id]
  );

  // Submit to School Locker
  async function submitToSchoolLocker() {
    // Layer 1: synchronous ref-based lock.
    if (slSubmittingRef.current) return;
    if (slSubmitState === "success" || slSubmitState === "loading") return;
    slSubmittingRef.current = true;
    // Layer 2: DB check — confirm we haven't already sent for this request.
    try {
      const prior = await getSentSubmissionByRequestId(req.id);
      if (prior) {
        slSubmittingRef.current = false;
        setSlSubmitState("success");
        return;
      }
    } catch (_e) {
      /* fall through and try to send */
    }
    setSlSubmitState("loading");
    setSlSubmitMsg("");

    const slChildren: SLPushChild[] = [];
    (req.children || []).forEach(function (child) {
      if (child.uniforms && child.uniforms.supplier === "School Locker") {
        slChildren.push({
          gender: child.gender || "",
          yearLevel: child.yearLevel || "",
          schoolName: child.schoolName || "",
          items: child.uniforms.items || {},
          customItems: child.uniforms.customItems || [],
          mobile: child.uniforms.mobile || "",
          email: child.uniforms.email || "",
        });
      }
    });

    let orderTotal = 0;
    slChildren.forEach(function (child) {
      const UNIFORM_ITEMS = [
        "Formal Shirt",
        "Formal Shorts",
        "Formal Skirt",
        "Sports/Polo Shirt",
        "Sports Shorts",
        "Sports Skirt/Skort",
        "Dress",
        "Sun-Safe Everyday Hat",
        "Sports Hat",
        "Tie",
        "Socks",
        "Backpack",
      ];
      const WINTER_ITEMS = ["Track Jacket", "Track Pants", "Jumper"];
      UNIFORM_ITEMS.concat(WINTER_ITEMS).forEach(function (k) {
        const d: SLLineItem = child.items?.[k] || {};
        orderTotal += (parseInt(d.qty as string, 10) || 0) * (parseFloat(d.cost as string) || 0);
      });
      (child.customItems || []).forEach(function (it) {
        orderTotal += (parseInt(it.qty as string, 10) || 0) * (parseFloat(it.cost as string) || 0);
      });
    });

    const payload = {
      refNo: req.refNo,
      requestDate: req.date || new Date().toISOString().slice(0, 10),
      shelterCode: req.shelterCode || "",
      shelterName: req.shelterName || "",
      shelterRef: req.shelterRef || "",
      notificationEmail: (function () {
        const emails: string[] = [];
        slChildren.forEach(function (c) {
          if (c.email && emails.indexOf(c.email) < 0) emails.push(c.email);
        });
        return emails.join(", ") || req.shelterEmail || "";
      })(),
      notificationSms: (function () {
        const nums: string[] = [];
        slChildren.forEach(function (c) {
          if (c.mobile && nums.indexOf(c.mobile) < 0) nums.push(c.mobile);
        });
        return nums.join(", ") || req.phone || "";
      })(),
      orderTotal: money(orderTotal),
      submittedBy: user.shelterName || user.email || "",
      children: slChildren,
    };

    // F4: send-email requires a verified caller — send the user's session JWT, not the public
    // anon key (the apikey header stays the anon key).
    let authToken = import.meta.env.VITE_SUPABASE_ANON_KEY;
    try {
      const sess = await getSupabaseClient().auth.getSession();
      if (sess.data?.session?.access_token) authToken = sess.data.session.access_token;
    } catch (_e) {
      /* no session → anon fallback (server rejects it) */
    }

    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
        method: "POST",
        // Supabase Edge Functions verify JWT by default; the anon key is the JWT
        // supabase.functions.invoke sends. Without these headers the gateway
        // rejects with HTTP 401 BEFORE the function runs — which was silently
        // failing every School Locker submission. utils/actions.ts sendEmail
        // already carries the same headers; this path was missed.
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ emailType: "school_locker_order", payload: payload }),
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        setSlSubmitState("error");
        setSlSubmitMsg("Submission failed — please try again or contact support.");
        // Record failure
        insertSubmission({
          request_id: req.id,
          ref_no: req.refNo,
          submitted_by: user.shelterName || user.email || "",
          status: "failed",
          failure_reason: data.error || `HTTP ${res.status}`,
          email_body_snapshot: "",
        });
        slSubmittingRef.current = false; // release lock so retries are possible
        return;
      }

      const submittedAt = new Date();
      const submittedAtStr = submittedAt.toLocaleString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      // Record success
      insertSubmission({
        request_id: req.id,
        ref_no: req.refNo,
        submitted_by: user.shelterName || user.email || "",
        status: "sent",
        smtp2go_message_id: data.messageId || null,
        email_body_snapshot: data.emailBody || "",
      }).then(function (r) {
        if (r.error) logger.warn("[SL] Audit insert failed:", r.error.message, r.error);
        // audit record saved successfully
      });

      setSlSubmitState("success");
      setSlSubmitMsg(submittedAtStr);
      // Lock stays set on success — we never want to send again for this request.
    } catch (_e) {
      setSlSubmitState("error");
      setSlSubmitMsg("Submission failed — please try again or contact support.");
      slSubmittingRef.current = false; // release lock so retries are possible
    }
  }

  return { slSubmitState, slSubmitMsg, submitToSchoolLocker };
}
